<template>
  <div class="channel-details-card p-2 mb-2 cal-channel-slot" :class="{ 'is-editing': editing, 'is-empty': !hasData && !editing, 'has-conflict': hasConflict, 'is-inactive': isInactive, 'single-row': isTemplate }">
    <!-- Collapsed view: row 1 = what the feed is, row 2 = when it runs + actions.
         Default-source rows (.single-row) put both on one line on desktop. -->
    <div v-if="!editing" class="slot-summary">
      <div class="d-flex align-items-center slot-summary-row" style="gap: 0.6rem;">
        <span class="source-label">Source {{ sourceIndex }}</span>
        <template v-if="hasData">
          <span v-if="showOrder && channel.orderId" class="order-badge" title="Order within the match">#{{ channel.orderId }}</span>
          <span class="lang-badge">{{ channel.language }}</span>
          <span v-if="onAir" class="on-air-badge" title="The feed window is open - only the order can be changed until it ends (Edit)">on air</span>
          <span class="text-truncate slot-shrink" style="max-width:160px;" :title="channelName">{{ channelName }}</span>
          <span class="text-muted">&middot;</span>
          <span class="text-truncate slot-shrink" style="max-width:110px;" :title="channel.origin">{{ channel.origin || '—' }}</span>
          <span class="text-muted">&middot;</span>
          <span class="text-truncate slot-shrink" style="max-width:110px;" :title="channel.encoder">{{ channel.encoder }}</span>
          <span class="text-muted">&middot;</span>
          <span class="text-truncate slot-shrink slot-shrink--stream" style="max-width:260px;" :title="streamLabel">{{ streamLabel }}</span>
        </template>
        <template v-else>
          <!-- Source 2+ stays locked until the source before it is saved (see channelOptionsForRow). -->
          <span v-if="locked" class="text-muted fst-italic">Save Source {{ sourceIndex - 1 }} first</span>
          <span v-else class="text-muted fst-italic">Not set up</span>
          <a v-if="!isReadOnly" role="button" class="procricket-btn--reverse btn-xs ms-auto" :class="{ disabled: locked }" @click="startEdit">Set up</a>
          <a v-if="canUseDefault && !isReadOnly" role="button" class="procricket-btn--reverse btn-xs" :class="{ disabled: saving || locked }" @click="useDefault">
            Use default
          </a>
        </template>
      </div>

      <div v-if="hasData" class="d-flex align-items-center slot-summary-row mt-1" style="gap: 0.6rem;">
        <span v-if="isTemplate" class="text-truncate time-badge" title="Lead / trail minutes applied against each match's own start time">
          {{ channel.leadMinutes }}m before → {{ channel.trailMinutes }}m after
        </span>
        <span v-else class="text-truncate time-badge" :title="'Feed up ' + formatDisplay(savedFeedStart) + ' — down ' + formatDisplay(savedFeedEnd) + ' (' + channel.leadMinutes + 'm before the match start / ' + channel.trailMinutes + 'm after its end)'">
          {{ formatDisplay(savedFeedStart) }} → {{ formatDisplay(savedFeedEnd) }}
        </span>

        <span v-if="hasConflict" class="conflict-badge" :class="{ clickable: !!conflictPartner }" :title="conflictText" @click="goToConflict">⚠ conflict</span>
        <span v-if="savedOrderDuplicate" class="order-warning-badge" :title="orderWarningText(channel.orderId)">⚠ same order</span>

        <label class="ms-auto d-flex align-items-center mb-0 active-toggle" style="gap:0.25rem;">
          <input type="checkbox" :checked="channel.active" :disabled="saving || isReadOnly || onAir" @change="toggleActive($event.target.checked)">
          <span class="small">Active</span>
        </label>
        <a v-if="!isReadOnly" role="button" class="procricket-btn--reverse btn-xs" @click="startEdit">Edit</a>
      </div>

      <span v-if="errorMessage" class="error-message small d-block mt-1">{{ errorMessage }}</span>
    </div>

    <!-- Edit form: two rows on desktop, stacked fields on mobile - both bind the same fields. -->
    <div v-else>
      <!-- Row 1: what the feed is. Row 2: when it runs + actions. -->
      <div class="edit-form-desktop d-flex flex-column" :class="{ 'edit-one-row': !showOrder }" style="gap: 0.5rem;">
        <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem;">
          <div class="text-nowrap"><span class="source-label">Source {{ sourceIndex }}</span></div>

          <div class="d-flex align-items-center text-nowrap">
            <select class="form-select form-select-sm" style="width: 120px;" v-model="formChannelName" :disabled="fieldsLocked" title="Channel (Automatic = the system gives a free one)">
              <option v-if="channelAutomatic" value="">Automatic</option>
              <option v-else :value="undefined" disabled>Channel</option>
              <option v-for="c in channelOptions" :key="c.channelId" :value="c.channelName" :class="{ 'option-taken': !!channelTakenLabel(c.channelName) }">{{ c.channelName }}{{ channelTakenLabel(c.channelName) }}</option>
            </select>
          </div>

          <div v-if="showOrder" class="d-flex align-items-center text-nowrap" style="gap: 0.3rem;">
            <label class="small mb-0" title="Position of this source within the match">Order</label>
            <input type="number" min="1" class="form-control form-control-sm minutes-input" style="width: 56px;" v-model.number="formOrderId" placeholder="auto" title="Position of this source within the match (empty = next free)">
          </div>

          <div class="d-flex align-items-center text-nowrap">
            <select class="form-select form-select-sm" style="width: 120px;" v-model="formOrigin" :disabled="fieldsLocked" title="Origin">
              <option :value="undefined" disabled>Origin</option>
              <option v-for="o in originOptions" :key="o" :value="o">{{ o }}</option>
            </select>
          </div>

          <div class="d-flex align-items-center text-nowrap">
            <select class="form-select form-select-sm" style="width: 72px;" v-model="formLanguage" :disabled="fieldsLocked" title="Language">
              <option :value="undefined" disabled>Language</option>
              <option v-for="l in languageOptions" :key="l" :value="l">{{ l }}</option>
            </select>
          </div>

          <div class="d-flex align-items-center text-nowrap" style="flex: 1 1 0; min-width: 110px;">
            <select class="form-select form-select-sm" style="flex: 1 1 0; min-width: 0;" v-model="formEncoder" :disabled="fieldsLocked" @change="onEncoderChange" title="Encoder">
              <option :value="undefined" disabled>Encoder</option>
              <option v-for="e in encoderOptions" :key="e" :value="e">{{ e }}</option>
            </select>
          </div>

          <div class="d-flex align-items-center text-nowrap" style="flex: 2 1 0; min-width: 160px;">
            <select class="form-select form-select-sm" style="flex: 1 1 0; min-width: 0;" v-model="formStreamId" :disabled="!formEncoder || streamsLoading || fieldsLocked" @change="onStreamChange" title="Stream">
              <option :value="undefined" disabled>{{ streamsLoading ? 'Loading...' : 'Stream' }}</option>
              <option v-for="s in streamOptions" :key="s.id" :value="s.id" :class="{ 'option-taken': !!streamTakenLabel(formEncoder, s.id) }">{{ s.name }} ({{ s.id }}){{ streamTakenLabel(formEncoder, s.id) }}</option>
            </select>
          </div>
        </div>

        <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem;">
          <div class="d-flex align-items-center text-nowrap" style="gap: 0.3rem;">
            <label class="small mb-0" title="Minutes before match start">Lead</label>
            <input type="number" min="0" class="form-control form-control-sm minutes-input" style="width: 46px;" v-model.number="formLeadMinutes" :disabled="fieldsLocked" title="Minutes before match start to bring this feed up">
          </div>
          <div class="d-flex align-items-center text-nowrap" style="gap: 0.3rem;">
            <label class="small mb-0" title="Minutes after match start">Trail</label>
            <input type="number" min="0" class="form-control form-control-sm minutes-input" style="width: 46px;" v-model.number="formTrailMinutes" :disabled="fieldsLocked" title="Minutes after match start to bring this feed down">
          </div>
          <span class="small text-muted">min</span>

          <a role="button" class="procricket-btn--reverse btn-xs text-nowrap ms-auto" title="Discard this edit" @click="cancelEdit">Cancel</a>
          <a role="button" class="procricket-btn btn-xs text-nowrap" :class="{ disabled: !canSave || saving }" @click="save">
            {{ saving ? 'Saving...' : 'Save' }}
          </a>
        </div>
      </div>

      <div class="edit-form-mobile cal-channel-edit-form">
        <div class="d-flex align-items-center mb-2">
          <span class="source-label">Source {{ sourceIndex }}</span>
        </div>

        <div class="edit-field">
          <label class="small mb-0">Channel</label>
          <select class="form-select form-select-sm" v-model="formChannelName" :disabled="fieldsLocked" title="Channel (Automatic = the system gives a free one)">
            <option v-if="channelAutomatic" value="">Automatic</option>
            <option v-else :value="undefined" disabled>Select...</option>
            <option v-for="c in channelOptions" :key="c.channelId" :value="c.channelName" :class="{ 'option-taken': !!channelTakenLabel(c.channelName) }">{{ c.channelName }}{{ channelTakenLabel(c.channelName) }}</option>
          </select>
        </div>

        <div v-if="showOrder" class="edit-field">
          <label class="small mb-0">Order</label>
          <input type="number" min="1" class="form-control form-control-sm" v-model.number="formOrderId" placeholder="auto" title="Position of this source within the match (empty = next free)">
        </div>

        <div class="edit-field">
          <label class="small mb-0">Origin</label>
          <select class="form-select form-select-sm" v-model="formOrigin" :disabled="fieldsLocked" title="Origin">
            <option :value="undefined" disabled>Select...</option>
            <option v-for="o in originOptions" :key="o" :value="o">{{ o }}</option>
          </select>
        </div>

        <div class="edit-field">
          <label class="small mb-0">Language</label>
          <select class="form-select form-select-sm" v-model="formLanguage" :disabled="fieldsLocked" title="Language">
            <option v-for="l in languageOptions" :key="l" :value="l">{{ l }}</option>
          </select>
        </div>

        <div class="edit-field">
          <label class="small mb-0">Encoder</label>
          <select class="form-select form-select-sm" v-model="formEncoder" :disabled="fieldsLocked" @change="onEncoderChange" title="Encoder">
            <option :value="undefined" disabled>Select...</option>
            <option v-for="e in encoderOptions" :key="e" :value="e">{{ e }}</option>
          </select>
        </div>

        <div class="edit-field">
          <label class="small mb-0">Stream</label>
          <select class="form-select form-select-sm" v-model="formStreamId" :disabled="!formEncoder || streamsLoading || fieldsLocked" @change="onStreamChange" title="Stream">
            <option :value="undefined" disabled>{{ streamsLoading ? 'Loading...' : 'Select...' }}</option>
            <option v-for="s in streamOptions" :key="s.id" :value="s.id" :class="{ 'option-taken': !!streamTakenLabel(formEncoder, s.id) }">{{ s.name }} ({{ s.id }}){{ streamTakenLabel(formEncoder, s.id) }}</option>
          </select>
        </div>

        <div class="edit-field-pair">
          <div class="edit-field">
            <label class="small mb-0" title="Minutes before match start">Lead (min)</label>
            <input type="number" min="0" class="form-control form-control-sm" v-model.number="formLeadMinutes" :disabled="fieldsLocked" title="Minutes before match start to bring this feed up">
          </div>
          <div class="edit-field">
            <label class="small mb-0" title="Minutes after match start">Trail (min)</label>
            <input type="number" min="0" class="form-control form-control-sm" v-model.number="formTrailMinutes" :disabled="fieldsLocked" title="Minutes after match start to bring this feed down">
          </div>
        </div>

        <a role="button" class="procricket-btn btn-xs w-100 text-center mt-2" :class="{ disabled: !canSave || saving }" @click="save">
          {{ saving ? 'Saving...' : 'Save' }}
        </a>
        <a role="button" class="procricket-btn--reverse btn-xs d-block text-center mt-1" title="Discard this edit" @click="cancelEdit">Cancel</a>
      </div>

      <div v-if="fieldsLocked" class="small text-muted mt-1">The feed is on air: only its order can be changed until its window ends.</div>
      <div v-if="hasConflict" class="conflict-message small mt-1" :class="{ clickable: !!conflictPartner }" @click="goToConflict">{{ conflictText }}</div>
      <div v-if="editOrderDuplicate" class="order-warning-message small mt-1">{{ orderWarningText(normalizedOrderId) }}</div>
      <div v-if="errorMessage" class="error-message small mt-1">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import XaccelService from '@/services/xaccel-service'
import { offsetIsoMinutes, formatLocalDateTime, feedWindowMs } from '@/utils/general-functions'

export default defineComponent({
  name: 'CalChannelSlotEditor',
  props: {
    // Display only ("Source N") - cal_channel has no sourceIndex column.
    sourceIndex: { type: Number, required: true },
    channel: { type: Object, default: null },
    channelOptions: { type: Array, default: () => [] },
    hardcodedData: { type: Object, default: () => ({ encoders: [], origins: [] }) },
    // Default template's value for this sourceIndex - enables "Use default".
    defaultSlot: { type: Object, default: null },
    // True for the "Default sources" rows (lead/trail minutes instead of absolute times).
    isTemplate: { type: Boolean, default: false },
    // Match start/end - the window preview (and its conflict check) while lead/trail minutes are edited.
    matchStartTime: { type: String, default: undefined },
    matchEndTime: { type: String, default: undefined },
    // BE conflict info for this saved row (null when it isn't in any overlap).
    channelConflict: { type: Object, default: null },
    // Channel rows the parent has loaded - used to mark taken channels/streams while editing.
    knownChannels: { type: Array, default: () => [] },
    // The matches of knownChannels - their feed windows are derived from the match times.
    knownMatches: { type: Array, default: () => [] },
    // Shows the order id (badge, input and the same-order warning).
    showOrder: { type: Boolean, default: false },
    // This match's own cal_channel rows - used to warn when two active sources share an order id.
    matchChannels: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
    defaultLeadMinutes: { type: Number, default: 15 },
    defaultTrailMinutes: { type: Number, default: 15 },
    languageOptions: { type: Array, default: () => [] },
    // Locked until the source before it is saved (see CalMatches.vue isSourceLocked).
    locked: { type: Boolean, default: false },
    // Finished match: show the saved source only, nothing can be saved or toggled.
    readOnly: { type: Boolean, default: false }
  },
  emits: ['save', 'go-to-conflict', 'remove'],
  data() {
    return {
      // An empty slot opens straight into edit, unless it's locked.
      editing: !this.hasDataFor(this.channel) && !this.locked && !this.readOnly,
      // '' = Automatic (new source): the backend gives a free channel. A name = picked by hand.
      formChannelName: '',
      formOrderId: undefined,
      formOrigin: undefined,
      formLanguage: 'EN',
      formEncoder: undefined,
      formStreamId: undefined,
      formStreamName: undefined,
      formLeadMinutes: this.defaultLeadMinutes,
      formTrailMinutes: this.defaultTrailMinutes,
      streamOptions: [],
      streamsLoading: false
    }
  },
  computed: {
    hasData() {
      // A default source may leave the channel on Automatic, so its channel is optional.
      return this.hasDataFor(this.channel, this.isTemplate);
    },
    // A source not saved yet may leave the channel to the backend; a saved one always has a channel.
    channelAutomatic() {
      return this.isTemplate || !this.hasData;
    },
    isReadOnly() {
      return this.readOnly;
    },
    // On air (the backend's onAir: the feed window holds now): encoder, stream, channel, times and active stay as they
    // are, only the order can change - the backend refuses anything else.
    onAir() {
      return !this.isTemplate && !!this.channel?.calChannelId && !!this.channel?.onAir;
    },
    fieldsLocked() {
      return this.onAir;
    },
    // A saved source that is switched off: the card is greyed and only its Active checkbox works.
    isInactive() {
      return !this.isTemplate && this.hasData && this.channel.active === false;
    },
    canUseDefault() {
      return !this.saving && !this.locked && this.hasDataFor(this.defaultSlot, true);
    },
    canSave() {
      if (!((this.formChannelName || this.channelAutomatic) && this.formOrigin && this.formLanguage && this.formEncoder && this.formStreamId)) {
        return false;
      }
      return this.formLeadMinutes !== undefined && this.formLeadMinutes !== null &&
          this.formTrailMinutes !== undefined && this.formTrailMinutes !== null;
    },
    encoderOptions() {
      return Array.isArray(this.hardcodedData?.encoders) ? this.hardcodedData.encoders : [];
    },
    originOptions() {
      return Array.isArray(this.hardcodedData?.origins) ? this.hardcodedData.origins : [];
    },
    channelName() {
      return this.channel?.channelName || (this.isTemplate ? 'Automatic' : '—');
    },
    streamLabel() {
      if (!this.channel) return '—';
      return `${this.channel.encStreamName || this.channel.encStreamId} (${this.channel.encStreamId})`;
    },
    hasConflict() {
      return !!this.channelConflict;
    },
    // [start, end] (ms) this slot would occupy if saved now - drives the "taken" markers.
    formWindow() {
      if (this.isTemplate || !this.matchStartTime) return null;
      const trailAnchor = this.matchEndTime || this.matchStartTime;
      const startIso = offsetIsoMinutes(this.matchStartTime, -Math.abs(this.formLeadMinutes || 0));
      const endIso = offsetIsoMinutes(trailAnchor, Math.abs(this.formTrailMinutes || 0));
      const start = startIso ? Date.parse(startIso) : NaN;
      const end = endIso ? Date.parse(endIso) : NaN;
      return Number.isNaN(start) || Number.isNaN(end) ? null : { start, end };
    },
    // The saved source's window (match times + its saved lead/trail minutes), for the summary badge.
    savedFeedStart() {
      return offsetIsoMinutes(this.matchStartTime, -(this.channel?.leadMinutes || 0));
    },
    savedFeedEnd() {
      return offsetIsoMinutes(this.matchEndTime || this.matchStartTime, this.channel?.trailMinutes || 0);
    },
    // Other active rows overlapping formWindow (same rule as the BE conflict check).
    takenRows() {
      const w = this.formWindow;
      if (!w) return [];
      const selfId = this.channel ? this.channel.calChannelId : undefined;
      const matchById = new Map((this.knownMatches || []).map(m => [m.calMatchId, m]));
      return (this.knownChannels || []).filter(c => {
        if (!c.active || (selfId !== undefined && c.calChannelId === selfId)) return false;
        const cw = feedWindowMs(c, matchById.get(c.calMatchId));
        return !!cw && cw.start < w.end && w.start < cw.end;
      });
    },
    normalizedOrderId() {
      const n = this.formOrderId;
      return Number.isInteger(n) && n >= 1 ? n : undefined;
    },
    // Another active source in this match with the same order id: warning only, never blocks.
    savedOrderDuplicate() {
      return !!(this.showOrder && this.channel && this.channel.active && this.orderClash(this.channel.orderId));
    },
    editOrderDuplicate() {
      return this.showOrder && this.normalizedOrderId !== undefined && this.orderClash(this.normalizedOrderId);
    },
    // First other match in the conflict - the click target (undefined for a same-match duplicate).
    conflictPartner() {
      const matches = this.channelConflict?.conflictingMatches || [];
      return matches.length ? matches[0] : undefined;
    },
    conflictText() {
      if (!this.channelConflict) return '';
      const axes = [];
      if (this.channelConflict.channelNameConflict) axes.push('channel slot');
      if (this.channelConflict.streamConflict) axes.push('encoder stream');
      const base = `Overlaps another match's ${axes.join(' and ')}`;
      return this.conflictPartner ? `${base} (match #${this.conflictPartner.calMatchId}) - click to view` : `${base} elsewhere`;
    }
  },
  watch: {
    // The match finished while the form was open: close it.
    isReadOnly(now) {
      if (now) this.editing = false;
    },
    // Close the form once the parent's save finishes without an error.
    saving(newVal, oldVal) {
      if (oldVal && !newVal && !this.errorMessage) {
        this.editing = false;
      }
    },
    // An empty slot opens straight into edit before the options load: preset once they arrive.
    hardcodedData: {
      immediate: true,
      handler() {
        if (!this.editing || this.hasData) return;
        const hadEncoder = !!this.formEncoder;
        this.applyPresets();
        if (!hadEncoder && this.formEncoder) this.loadStreams(this.formEncoder);
      }
    },
    // Open straight into edit as soon as the source before this one is saved.
    locked(newVal, oldVal) {
      if (oldVal && !newVal && !this.hasData) {
        this.startEdit();
      }
    }
  },
  methods: {
    orderClash(orderId) {
      if (orderId === undefined || orderId === null) return false;
      const selfId = this.channel ? this.channel.calChannelId : undefined;
      return this.matchChannels.some(c => c.active && c.orderId === orderId && (selfId === undefined || c.calChannelId !== selfId));
    },
    orderWarningText(orderId) {
      return `Another source in this match also has order ${orderId}`;
    },
    takenByText(rows) {
      const ids = [...new Set(rows.map(r => r.calMatchId))];
      return ids.map(id => (id === this.channel?.calMatchId ? 'this match' : `match #${id}`)).join(', ');
    },
    channelTakenLabel(channelName) {
      const rows = this.takenRows.filter(r => r.channelName === channelName);
      return rows.length ? ` — taken (${this.takenByText(rows)})` : '';
    },
    streamTakenLabel(encoder, streamId) {
      const rows = this.takenRows.filter(r => r.encoder === encoder && String(r.encStreamId) === String(streamId));
      return rows.length ? ` — taken (${this.takenByText(rows)})` : '';
    },
    // Preset for a fresh source, by position: 1st source -> first option, 2nd -> second, 3rd -> first again.
    presetOption(options) {
      if (!options.length) return undefined;
      return options[(this.sourceIndex - 1) % Math.min(2, options.length)];
    },
    applyPresets() {
      if (!this.formOrigin) this.formOrigin = this.presetOption(this.originOptions);
      if (!this.formEncoder) this.formEncoder = this.presetOption(this.encoderOptions);
    },
    hasDataFor(slot, channelOptional = false) {
      return !!(slot && (channelOptional || slot.channelName) && slot.origin && slot.encoder && slot.encStreamId);
    },
    startEdit() {
      if (this.locked || this.isReadOnly) return;
      const source = this.hasData ? this.channel : (this.hasDataFor(this.defaultSlot) ? this.defaultSlot : null);
      this.formChannelName = source ? (source.channelName || '') : '';
      this.formOrderId = this.hasData ? this.channel.orderId : undefined;
      this.formOrigin = source?.origin;
      this.formLanguage = source?.language || 'EN';
      this.formEncoder = source?.encoder;
      this.formStreamId = source?.encStreamId;
      this.formStreamName = source?.encStreamName;
      this.streamOptions = [];
      if (!source) this.applyPresets();
      if (this.formEncoder) {
        this.loadStreams(this.formEncoder);
      }
      if (this.isTemplate) {
        this.formLeadMinutes = source?.leadMinutes ?? this.defaultLeadMinutes;
        this.formTrailMinutes = source?.trailMinutes ?? this.defaultTrailMinutes;
      } else if (this.hasData) {
        // Saved slot: its own lead/trail minutes.
        this.formLeadMinutes = this.channel.leadMinutes ?? this.defaultLeadMinutes;
        this.formTrailMinutes = this.channel.trailMinutes ?? this.defaultTrailMinutes;
      } else if (this.hasDataFor(this.defaultSlot)) {
        this.formLeadMinutes = this.defaultSlot.leadMinutes ?? this.defaultLeadMinutes;
        this.formTrailMinutes = this.defaultSlot.trailMinutes ?? this.defaultTrailMinutes;
      } else {
        this.formLeadMinutes = this.defaultLeadMinutes;
        this.formTrailMinutes = this.defaultTrailMinutes;
      }
      this.editing = true;
    },
    cancelEdit() {
      // A never-saved slot is removed on cancel; template rows just collapse.
      if (!this.hasData && !this.isTemplate && this.sourceIndex !== 1) {
        this.$emit('remove', { calChannelId: undefined });
        return;
      }
      this.editing = false;
    },
    onEncoderChange() {
      this.formStreamId = undefined;
      this.formStreamName = undefined;
      this.streamOptions = [];
      if (this.formEncoder) {
        this.loadStreams(this.formEncoder);
      }
    },
    loadStreams(encoder) {
      this.streamsLoading = true;
      XaccelService.getXaccelStreamIds(encoder, true).then((res) => {
        const streams = (res && res.streams) || [];
        this.streamOptions = streams.map(s => ({ id: s.id, name: s.name }));
      }).catch(() => {
        this.streamOptions = [];
      }).finally(() => {
        this.streamsLoading = false;
      });
    },
    onStreamChange() {
      const found = this.streamOptions.find(s => s.id === this.formStreamId);
      this.formStreamName = found ? found.name : undefined;
    },
    toggleActive(checked) {
      if (this.isReadOnly || this.onAir) return;
      // Timing stays as it is; calChannelId lets the parent find this exact row.
      this.$emit('save', {
        sourceIndex: this.sourceIndex,
        calChannelId: this.channel.calChannelId,
        channelName: this.channel.channelName,
        orderId: this.channel.orderId,
        origin: this.channel.origin,
        encoder: this.channel.encoder,
        encStreamId: this.channel.encStreamId,
        encStreamName: this.channel.encStreamName,
        language: this.channel.language,
        leadMinutes: this.channel.leadMinutes,
        trailMinutes: this.channel.trailMinutes,
        active: checked
      });
    },
    // The parent decides whether to scroll to the other match or navigate to it.
    goToConflict() {
      if (!this.conflictPartner) return;
      this.$emit('go-to-conflict', this.conflictPartner);
    },
    useDefault() {
      if (this.locked || this.isReadOnly) return;
      this.$emit('save', {
        sourceIndex: this.sourceIndex,
        channelName: this.defaultSlot.channelName,
        origin: this.defaultSlot.origin,
        encoder: this.defaultSlot.encoder,
        encStreamId: this.defaultSlot.encStreamId,
        encStreamName: this.defaultSlot.encStreamName,
        language: this.defaultSlot.language,
        leadMinutes: this.defaultSlot.leadMinutes,
        trailMinutes: this.defaultSlot.trailMinutes,
        active: true
      });
    },
    save() {
      if (!this.canSave || this.saving || this.locked || this.isReadOnly) return;
      this.$emit('save', {
        sourceIndex: this.sourceIndex,
        // Set for an already-saved row, so changing its channel updates it instead of creating a new one.
        calChannelId: this.channel?.calChannelId,
        channelName: this.formChannelName || undefined,
        orderId: this.showOrder ? this.normalizedOrderId : undefined,
        origin: this.formOrigin,
        encoder: this.formEncoder,
        encStreamId: this.formStreamId,
        encStreamName: this.formStreamName,
        language: this.formLanguage,
        leadMinutes: this.formLeadMinutes,
        trailMinutes: this.formTrailMinutes,
        active: true
      });
    },
    formatDisplay(iso) {
      return formatLocalDateTime(iso) || '—';
    }
  }
})
</script>

<style src="@/assets/style.css"></style>
<style scoped>
.option-taken {
  color: #dc3545;
}
/* Tighter padding so a 2-3 digit value fits the 46px input. */
.minutes-input {
  padding: 0.15rem 0.25rem;
}
</style>
